import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MapPin, Calendar, Coins, Home as HomeIcon, Utensils, Users, Loader2, Heart, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/seo/SEO";
import api, { API, formatApiErrorDetail } from "@/lib/api";

export default function OfferDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [applied, setApplied] = useState(false);
  const [applying, setApplying] = useState(false);
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    api.get(`/offers/${id}`).then(({ data }) => setOffer(data));
  }, [id]);

  const handleToggleFavorite = async () => {
    try {
      const { data } = await api.post("/favorites/toggle", { target_type: "offer", target_id: id });
      setFavorited(data.favorited);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      await api.post("/applications", { offer_id: id, origin_url: window.location.origin });
      setApplied(true);
      toast.success(t("application.applicationSent"));
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail, t);
      if (e.response?.status === 400) setApplied(true);
      else toast.error(detail);
    } finally {
      setApplying(false);
    }
  };

  if (!offer) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#4ba3b7]" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="offer-detail-page">
      <SEO title={`${t(`lookups.missionTypes.${offer.mission_type}`)} - ${t(`lookups.regions.${offer.region}`)} | JobVinum`}
           description={offer.description?.slice(0, 160) || t("search.offersTitle")} path={`/offers/${id}`} />
      <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6 sm:p-8">
        <div className="flex items-start justify-between mb-4">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">{t(`lookups.missionTypes.${offer.mission_type}`)}</h1>
          <div className="flex items-center gap-2">
            {offer.is_boosted && <Badge className="bg-[#dde165]/25 text-[#2B2B2B] border-0">{t("common.urgent")}</Badge>}
            {user?.role === "candidate" && (
              <button onClick={handleToggleFavorite} data-testid="offer-detail-favorite-toggle-button" aria-label="favorite">
                <Heart className={`h-6 w-6 ${favorited ? "fill-[#FF3B30] text-[#FF3B30]" : "text-[#2B2B2B]/30"}`} />
              </button>
            )}
          </div>
        </div>

        {offer.producer?.company_name && (
          <div className="flex items-center gap-3 mb-6">
            {offer.producer.logo_path && (
              <img src={`${API}/profiles/files/${offer.producer.logo_path}`} alt="" className="h-10 w-10 rounded-lg object-cover" />
            )}
            <span className="font-semibold text-[#2B2B2B]/80">{offer.producer.company_name}</span>
            {offer.producer.rating?.average && (
              <div className="flex items-center gap-1 text-sm" data-testid="offer-detail-producer-rating">
                <Star className="h-4 w-4 fill-[#dde165] text-[#dde165]" />
                <span className="font-semibold">{offer.producer.rating.average}</span>
                <span className="text-[#2B2B2B]/40">({offer.producer.rating.count})</span>
              </div>
            )}
          </div>
        )}

        {offer.photos?.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-6" data-testid="offer-detail-photos-gallery">
            {offer.photos.map((path, idx) => (
              <a key={path} href={`${API}/profiles/files/${path}`} target="_blank" rel="noreferrer"
                 className={`rounded-xl overflow-hidden ${idx === 0 ? "col-span-3 aspect-[16/9]" : "aspect-square"}`}
                 data-testid={`offer-detail-photo-${idx}`}>
                <img src={`${API}/profiles/files/${path}`} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform duration-300" />
              </a>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-8">
          <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#4ba3b7]" /> {t(`lookups.regions.${offer.region}`)}, {t(`lookups.countries.${offer.country}`)}</div>
          <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#4ba3b7]" /> {offer.date_start} &rarr; {offer.date_end}</div>
          <div className="flex items-center gap-2"><Coins className="h-4 w-4 text-[#4ba3b7]" /> {offer.salary_amount}&euro; / {t(`lookups.salaryPeriods.${offer.salary_period}`)}</div>
          <div className="flex items-center gap-2"><HomeIcon className="h-4 w-4 text-[#4ba3b7]" /> {t(`lookups.housingOptions.${offer.housing}`)}</div>
          <div className="flex items-center gap-2"><Utensils className="h-4 w-4 text-[#4ba3b7]" /> {t(`lookups.mealsOptions.${offer.meals}`)}</div>
          <div className="flex items-center gap-2"><Users className="h-4 w-4 text-[#4ba3b7]" /> {offer.positions_count} {t("offer.positionsCount").toLowerCase()}</div>
        </div>

        {offer.skills_wanted?.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-wide text-[#2B2B2B]/50 mb-2">{t("offer.skillsWanted")}</p>
            <div className="flex flex-wrap gap-2">
              {offer.skills_wanted.map((s) => <Badge key={s} variant="outline" className="rounded-full">{t(`lookups.skills.${s}`)}</Badge>)}
            </div>
          </div>
        )}

        {offer.additional_info && (
          <p className="text-sm text-[#2B2B2B]/70 mb-8 whitespace-pre-line">{offer.additional_info}</p>
        )}

        {user === false ? (
          <Button asChild className="h-12 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold" data-testid="offer-detail-login-to-apply-button">
            <a href="/login">{t("application.loginToApply")}</a>
          </Button>
        ) : user?.role === "candidate" ? (
          <Button onClick={handleApply} disabled={applied || applying} className="h-12 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold" data-testid="offer-detail-apply-button">
            {applied ? t("application.alreadyApplied") : applying ? t("common.loading") : t("application.applyNow")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
